/** Parts authenticity — original vs replaced, with OEM / aftermarket quality. */

export const PART_IDS = [
  'display',
  'battery',
  'camera',
  'housing',
  'back_glass',
  'face_id',
] as const

export type PartId = (typeof PART_IDS)[number]

export const PART_STATUSES = [
  'original',
  'replaced_oem',
  'replaced_aftermarket',
  'replaced',
  'unknown',
] as const

export type PartStatus = (typeof PART_STATUSES)[number]

export const DISPLAY_GRADES = ['premium_oled', 'oled', 'incell', 'lcd', 'unspecified'] as const
export type DisplayGrade = (typeof DISPLAY_GRADES)[number]

export type PartAuthenticity = {
  status: PartStatus
  grade?: string | null
}

export type PartsAuthenticityMap = Partial<Record<PartId, PartAuthenticity | null>>

export const EMPTY_PARTS_AUTHENTICITY: PartsAuthenticityMap = {
  display: { status: 'unknown' },
  battery: { status: 'unknown' },
  camera: { status: 'unknown' },
  housing: { status: 'unknown' },
  back_glass: { status: 'unknown' },
  face_id: { status: 'unknown' },
}

function isAppleBrand(manufacturer?: string | null): boolean {
  return /apple/i.test((manufacturer || '').trim())
}

/** Face ID is typical on Apple X and later, not SE / Touch ID models. */
export function showFaceIdPart(manufacturer?: string | null, modelName?: string | null): boolean {
  if (!isAppleBrand(manufacturer)) return false
  const n = `${modelName || ''}`.toLowerCase()
  if (/\bse\b/.test(n)) return false
  if (/iphone\s*([1-8])\b/.test(n) && !/iphone\s*(1[0-9]|x)/.test(n)) return false
  return true
}

export function visiblePartIds(
  manufacturer?: string | null,
  modelName?: string | null,
  parts?: PartsAuthenticityMap | null,
): PartId[] {
  return PART_IDS.filter((id) => {
    if (id !== 'face_id') return true
    if (showFaceIdPart(manufacturer, modelName)) return true
    return Boolean(parts?.face_id && parts.face_id.status !== 'unknown')
  })
}

export function isPartStatus(v: unknown): v is PartStatus {
  return typeof v === 'string' && (PART_STATUSES as readonly string[]).includes(v)
}

export function normalizePartEntry(raw: unknown): PartAuthenticity {
  if (!raw || typeof raw !== 'object') return { status: 'unknown' }
  const rec = raw as Record<string, unknown>
  const status = isPartStatus(rec.status) ? rec.status : 'unknown'
  const grade = typeof rec.grade === 'string' && rec.grade.trim() ? rec.grade.trim().slice(0, 48) : null
  return { status, grade }
}

export function normalizePartsAuthenticity(raw: unknown): PartsAuthenticityMap {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const out: PartsAuthenticityMap = {}
  for (const id of PART_IDS) {
    if (src[id] != null) out[id] = normalizePartEntry(src[id])
  }
  return out
}

/** Fill battery from Battery Transparency when the seller has not declared the part. */
export function mergeBatteryIntoParts(
  parts: PartsAuthenticityMap,
  batteryOriginal?: boolean | null,
): PartsAuthenticityMap {
  if (parts.battery && parts.battery.status !== 'unknown') return parts
  if (batteryOriginal === true) return { ...parts, battery: { status: 'original' } }
  if (batteryOriginal === false) return { ...parts, battery: { status: 'replaced' } }
  return parts
}

export function hasDeclaredParts(parts: PartsAuthenticityMap): boolean {
  return PART_IDS.some((id) => {
    const st = parts[id]?.status
    return st != null && st !== 'unknown'
  })
}

export function partTone(status: PartStatus | undefined): 'original' | 'oem' | 'aftermarket' | 'unknown' {
  if (status === 'original') return 'original'
  if (status === 'replaced_oem') return 'oem'
  if (status === 'replaced_aftermarket' || status === 'replaced') return 'aftermarket'
  return 'unknown'
}

export function batteryOriginalFromParts(parts: PartsAuthenticityMap): boolean | undefined {
  const st = parts.battery?.status
  if (st === 'original') return true
  if (st === 'replaced_oem' || st === 'replaced_aftermarket' || st === 'replaced') return false
  return undefined
}

export function compactPartsAuthenticity(parts: PartsAuthenticityMap): PartsAuthenticityMap {
  const out: PartsAuthenticityMap = {}
  for (const id of PART_IDS) {
    const entry = parts[id]
    if (!entry) continue
    out[id] = {
      status: isPartStatus(entry.status) ? entry.status : 'unknown',
      grade: entry.status === 'replaced_aftermarket' && entry.grade ? entry.grade : null,
    }
  }
  return out
}
